import { inject } from '@angular/core';
import { EcommerceTrackingMapperService } from '@epicuro-next/addons/ecommerce-tracking-mappers';
import { EcommerceActionsToTrack } from '@epicuro-next/platform/tracking';
import {
  gtmTrackActions,
  recommendationItemsShown,
} from '@epicuro-next/state/tracking';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { of, OperatorFunction, timer } from 'rxjs';
import {
  bufferTime,
  concatMap,
  filter,
  groupBy,
  map,
  mergeMap,
  switchMap,
  toArray,
} from 'rxjs/operators';

import { defaultCollectorSelect } from '../shared/default-collector-select';

export class RecommendationDataCollectorsEffects {
  public static createBufferTime: <T>(
    timeInMs: number,
  ) => OperatorFunction<T, T[]> = bufferTime;
  public static itemsShownBufferTime = 1000;

  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly ecommerceTrackingMapper = inject(
    EcommerceTrackingMapperService,
  );

  public recommendationItemsShownDataCollector$ = createEffect(() =>
    this.actions$.pipe(
      ofType(gtmTrackActions.gtmCollectTrackData),
      filter(
        (action) =>
          action.name === EcommerceActionsToTrack.RecommendationItemsShown,
      ),
      map((action) => {
        const payload = action.payload as unknown as ReturnType<
          typeof recommendationItemsShown
        >;

        return {
          ...action,
          payload,
        };
      }),
      RecommendationDataCollectorsEffects.createBufferTime(
        RecommendationDataCollectorsEffects.itemsShownBufferTime,
      ), // First buffer, then group. As bufferTime keeps emitting (hence the filter on the next line). If it would come after groupBy, there are is 1 stream per group that keeps emitting.
      filter((bufferedActions) => bufferedActions.length > 0),
      mergeMap((bufferedActions) => of(...bufferedActions)),
      groupBy((action) => this.serializeItemsShownAction(action.payload), {
        duration: () =>
          timer(RecommendationDataCollectorsEffects.itemsShownBufferTime * 2), // This is needed or groupBy does not complete the stream. Which could result in groups stacking up in memory over time: a memory leak.
      }),
      mergeMap((groupedActions$) => {
        return groupedActions$.pipe(
          toArray(), // toArray works here because the groupBy has a duration set and thereby completes a stream, and opens a new one after this duration.
          concatLatestFrom(() => [...defaultCollectorSelect(this.store)]),
          concatMap(
            ([actionsInBuffer, activeAccount, presetDisplayName, , user]) => {
              const allProducts = actionsInBuffer
                .map((action) => action.payload.products)
                .flat();

              const payload: ReturnType<typeof recommendationItemsShown> = {
                ...actionsInBuffer[0].payload,
                products: allProducts,
              };

              const eventMappingFunction =
                actionsInBuffer[0].eventMappingFunction;

              return this.ecommerceTrackingMapper
                .mapEventsFromAction(payload, eventMappingFunction)
                .pipe(
                  switchMap((eventNames) =>
                    eventNames.map((name) =>
                      gtmTrackActions.gtmSendTrackEvent({
                        name,
                        payload: {
                          ...payload,
                          activeAccount,
                          presetDisplayName,
                          user,
                        },
                      }),
                    ),
                  ),
                );
            },
          ),
        );
      }),
    ),
  );

  public recommendationItemClickedDataCollector$ = createEffect(() =>
    this.actions$.pipe(
      ofType(gtmTrackActions.gtmCollectTrackData),
      filter(
        (action) =>
          action.name === EcommerceActionsToTrack.RecommendationItemClicked,
      ),
      concatLatestFrom(() => [...defaultCollectorSelect(this.store)]),
      concatMap(
        ([
          { eventMappingFunction, payload },
          activeAccount,
          presetDisplayName,
          ,
          user,
        ]) =>
          this.ecommerceTrackingMapper
            .mapEventsFromAction(payload, eventMappingFunction)
            .pipe(
              switchMap((eventNames) =>
                eventNames.map((name) =>
                  gtmTrackActions.gtmSendTrackEvent({
                    name,
                    payload: {
                      ...payload,
                      activeAccount,
                      presetDisplayName,
                      user,
                    },
                  }),
                ),
              ),
            ),
      ),
    ),
  );

  private serializeItemsShownAction(
    payload: ReturnType<typeof recommendationItemsShown>,
  ): string {
    const subset: Omit<
      ReturnType<typeof recommendationItemsShown>,
      'products'
    > = {
      panelName: payload.panelName,
      panelStyle: payload.panelStyle,
      correlationId: payload.correlationId,
      servingConfig: payload.servingConfig,
      attributionToken: payload.attributionToken,
      type: payload.type,
      providerId: payload.providerId,
      hash: payload.hash,
    };

    return JSON.stringify(subset);
  }
}
